// `pdfnative extract` — extract a subset of pages from a PDF into a new PDF
// (pdfnative page-tree API). Page order follows the `--pages` selector and
// pages may repeat. Signatures/forms are dropped. Encrypted sources are
// supported with --password (v1.6.0); the output can be re-encrypted with
// --encrypt and streamed at constant memory with --stream (see `merge`).

import { extractPages, streamExtractPages, openPdf } from '../core-bridge/index.js';
import type { StreamMergeOptions } from '../core-bridge/index.js';
import { type ParsedArgs, getStringFlag, hasFlag } from '../utils/args.js';
import { readFileOrStdin, writeOutput, writeStreamingOutput } from '../utils/io.js';
import { CliError } from '../utils/error.js';
import { emitStatus, isDryRun } from '../utils/agent.js';
import {
    parseMaxOutputSize,
    resolveSourcePassword,
    readEncryptTrigger,
    buildEncryptOptions,
    parseChunkSize,
    mapPdfError,
} from '../utils/pdfops.js';
import { parsePageList } from '../utils/pages.js';

export async function extract(args: ParsedArgs): Promise<void> {
    const inputPath = getStringFlag(args.flags, 'input', 'i');
    const outputPath = getStringFlag(args.flags, 'output', 'o');
    const pagesSpec = getStringFlag(args.flags, 'pages');
    const dropAnnotations = hasFlag(args.flags, 'drop-annotations');
    const maxOutputSize = parseMaxOutputSize(getStringFlag(args.flags, 'max-output-size'));
    const password = resolveSourcePassword(args.flags);
    const { enabled: doEncrypt, algoRaw } = readEncryptTrigger(args.flags);
    const encrypt = doEncrypt ? buildEncryptOptions(args, algoRaw) : undefined;
    const stream = hasFlag(args.flags, 'stream');
    const chunkSize = parseChunkSize(getStringFlag(args.flags, 'chunk-size'));
    const dryRun = hasFlag(args.flags, 'dry-run') || isDryRun();

    if (pagesSpec === undefined) {
        throw new CliError('extract requires --pages <selector> (e.g. "1,3,5-7").', 2);
    }

    const inputBuf = await readFileOrStdin(inputPath);
    const pdfBytes = new Uint8Array(inputBuf);

    let pageCount: number;
    try {
        pageCount = openPdf(pdfBytes, password !== undefined ? { password } : undefined).pageCount;
    } catch (e) {
        throw mapPdfError(e, 'Failed to read PDF');
    }

    const indices = parsePageList(pagesSpec, pageCount);

    const opts: { -readonly [K in keyof StreamMergeOptions]: StreamMergeOptions[K] } = {
        dropAnnotations,
    };
    if (maxOutputSize !== undefined) opts.maxOutputSize = maxOutputSize;
    if (password !== undefined) opts.password = password;
    if (encrypt !== undefined) opts.encrypt = encrypt;
    if (chunkSize !== undefined) opts.chunkSize = chunkSize;

    if (dryRun) {
        emitStatus({
            command: 'extract',
            dryRun: true,
            pages: indices.length,
            encrypted: encrypt !== undefined,
            output: outputPath ?? '-',
        });
        return;
    }

    if (stream) {
        try {
            await writeStreamingOutput(streamExtractPages(pdfBytes, indices, opts), outputPath);
        } catch (e) {
            throw mapPdfError(e, 'Failed to extract pages');
        }
        emitStatus({
            command: 'extract',
            dryRun: false,
            pages: indices.length,
            streamed: true,
            encrypted: encrypt !== undefined,
            output: outputPath ?? '-',
        });
        return;
    }

    let extracted: Uint8Array;
    try {
        extracted = extractPages(pdfBytes, indices, opts);
    } catch (e) {
        throw mapPdfError(e, 'Failed to extract pages');
    }

    await writeOutput(extracted, outputPath);
    emitStatus({
        command: 'extract',
        dryRun: false,
        pages: indices.length,
        encrypted: encrypt !== undefined,
        output: outputPath ?? '-',
        bytes: extracted.length,
    });
}
